import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import './Search.css';

/**
 * Constants placed outside to prevent re-allocation and dependency issues.
 */
const SEARCH_MESSAGES = [
    "products that nobody asked for.",
    "solutions to problems that don't exist.",
    "items that should have stayed as sketches.",
    "masterpieces of pure inconvenience.",
    "brilliant wastes of engineering.",
    "perfect gifts for your worst enemy.",
    "ways to make your life 10% harder."
];

const SUPABASE_STORAGE_URL = 'https://tajpggkeslwptgqgswyi.supabase.co/storage/v1/object/public/product-images/';
const CART_STORAGE_KEY = 'uselessProductsCart';

const formatPrice = (value) => {
    return value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    });
};

const loadCart = () => {
    if (typeof window === 'undefined') return {};
    try {
        return JSON.parse(window.sessionStorage.getItem(CART_STORAGE_KEY) || '{}');
    } catch {
        return {};
    }
};

const saveCart = (cart) => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
};

function Search({ theme, toggleTheme, searchQuery, onBackClick, onCartUpdated }) {
    // 1. State Management
    const [searchResults, setSearchResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentTerm, setCurrentTerm] = useState(searchQuery);
    const [quantityById, setQuantityById] = useState({});
    const [cartMessage, setCartMessage] = useState('');
    const lastSearchTerm = useRef(""); // Tracks the last successful query

    // FIXED: Use state to hold the random message so it persists 
    // and doesn't require searchResults as a dependency.
    const [randomMessage] = useState(() =>
        SEARCH_MESSAGES[Math.floor(Math.random() * SEARCH_MESSAGES.length)]
    );

    /**
     * Data Normalization
     */
    const normalizeProduct = useCallback((product) => {
        if (!product) return null;
        return {
            id: product.id || Math.random(),
            name: product.productName ?? product.name ?? "Mystery Item",
            desc: product.description ?? product.desc ?? "No description available for this nonsense.",
            img: product.imagePath
                ? (product.imagePath.startsWith('http')
                    ? product.imagePath
                    : `${SUPABASE_STORAGE_URL}${product.imagePath}`)
                : null,
        };
    }, []);

    // 2. Fetch Logic
    const fetchSearchResults = useCallback(async (query, signal) => {
        const trimmedQuery = query?.trim();
        if (!trimmedQuery || trimmedQuery === lastSearchTerm.current) return;

        setIsLoading(true);
        setCurrentTerm(trimmedQuery);

        try {
            const response = await fetch(
                `https://useless-products-backend.onrender.com/api/v1/products/search?q=${encodeURIComponent(trimmedQuery)}&limit=12`,
                { signal } // Allows cancelling the request
            );

            if (!response.ok) throw new Error("Search failed");

            const data = await response.json();
            const products = data.products || [];
            // Filter out nulls from normalization if any
            setSearchResults(products.map(normalizeProduct).filter(Boolean));
        } catch (error) {
            if (error.name === 'AbortError') return;
            console.error("Error searching:", error);
            setSearchResults([]);
        } finally {
            setIsLoading(false);
        }
    }, [normalizeProduct]);

    // Initial search and cleanup
    useEffect(() => {
        const controller = new AbortController();
        fetchSearchResults(searchQuery, controller.signal);

        return () => controller.abort(); // Cancel request if component unmounts
    }, [searchQuery, fetchSearchResults]);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    const getProductPrice = (productId) => {
        const cart = loadCart();
        const existing = cart[String(productId)];
        return existing?.price ?? Math.floor(Math.random() * 1000001);
    };

    const handleQuantityChange = (productId, value) => {
        const normalized = Math.max(1, Number(value) || 1);
        setQuantityById((prev) => ({
            ...prev,
            [productId]: normalized,
        }));
    };

    const addToCart = (product, quantity) => {
        if (!product || quantity < 1) return;

        const id = String(product.id);
        const cart = loadCart();
        const existing = cart[id] || {};
        const unitPrice = existing.price ?? getProductPrice(id);

        cart[id] = {
            id,
            name: product.name,
            desc: product.desc,
            img: product.img || '',
            price: unitPrice,
            quantity: (existing.quantity || 0) + quantity,
        };

        saveCart(cart);
        if (typeof onCartUpdated === 'function') {
            onCartUpdated();
        }
        setQuantityById((prev) => ({ ...prev, [id]: 1 }));
        setCartMessage(`Added ${quantity} × ${product.name} at ${formatPrice(unitPrice)} each.`);
        window.setTimeout(() => setCartMessage(''), 3200);
    };


    /**
         * Soothing Border Colors (Muted Palette)
         * Dark Mode: Deep, desaturated jewel tones.
         * Light Mode: Soft, pastel "glass" tones.
         * No high-contrast oranges or jarring stand-outs.
         */
    const shuffledBorderColors = useMemo(() => {
        const soothingColors = theme === 'dark'
            ? [
                '#5c46c3', // Soft Indigo
                '#2d64b8', // Dusty Blue
                '#4e8e31', // Sage Green
                '#4a7a8c', // Slate Teal
                '#7d5ba6', // Muted Plum
                '#3e5d8a'  // Deep Steel Blue
            ]
            : [
                '#a579ff', // Pale Lavender
                '#63b5ff', // Sky Glass
                '#8be783', // Mint Leaf
                '#8ecae6', // Soft Cyan
                '#b8bedd', // Periwinkle Grey
                '#98c1d9'  // Pastel Cerulean
            ];

        // Fisher-Yates Shuffle for randomizing the order of colors each render
        const shuffled = [...soothingColors];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    }, [theme]);

    return (
        <div className={`app ${theme}`}>
            <main>
                <section className="search-header-section">
                    <div className="search-info">
                        <div className="search-text-wrapper">
                            <h2>
                                {isLoading ? "Searching..." : `Search Results for "${currentTerm || 'Everything'}"`}
                            </h2>

                            {!isLoading && (
                                <p className="result-count">
                                    Found {searchResults.length} {randomMessage}
                                </p>
                            )}
                        </div>

                        <button className="back-btn" onClick={onBackClick}>
                            Return to Gallery
                        </button>
                    </div>
                </section>

                {cartMessage && (
                    <div className="cart-feedback">
                        {cartMessage}
                    </div>
                )}

                <section className="product-section">
                    {searchResults.length === 0 && !isLoading ? (
                        <div className="no-results">
                            <p>I couldn't find anything that stupid. Try searching for something else?</p>
                        </div>
                    ) : (
                        <div className="product-grid">
                            {searchResults.map((product, index) => (
                                <article
                                    key={product.id}
                                    className="product-card"
                                    style={{
                                        borderColor: shuffledBorderColors[index % shuffledBorderColors.length]
                                    }}
                                >
                                    <div className="image-container">
                                        {product.img ? (
                                            /* If image exists, show it */
                                            <img src={product.img} alt={product.name} loading="lazy" />
                                        ) : (
                                            /* If image is missing, show this text box instead */
                                            <div className="no-image-fallback">
                                                <span>No image to load</span>
                                            </div>
                                        )}

                                    </div>
                                    <div className="product-copy">
                                        <h2 className="product-name">{product.name}</h2>
                                        <p className="product-desc">{product.desc}</p>
                                    </div>

                                    <div className="product-footer">
                                        <span className="product-price">{formatPrice(getProductPrice(product.id))}</span>
                                        <div className="product-actions">
                                            <label className="qty-label">
                                                Qty
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={quantityById[product.id] ?? 1}
                                                    onChange={(event) => handleQuantityChange(product.id, event.target.value)}
                                                />
                                            </label>
                                            <button
                                                className="add-cart-btn"
                                                onClick={() => addToCart(product, quantityById[product.id] ?? 1)}
                                            >
                                                Add to Cart
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                    {searchResults.length > 0 && (
                        <div className="view-more-container">
                            <button
                                className="back-btn view-more-btn"
                                onClick={scrollToTop}
                            >
                                Back to Top
                            </button>
                        </div>
                    )}

                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default Search;